import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import { useI18n } from "@/context/I18n";
import { useAuth } from "@/context/AuthContext";
import TrackRow from "@/components/music/TrackRow";
import RequestArtist from "@/components/music/RequestArtist";
import Carousel from "@/components/music/Carousel";
import PopularTracks from "@/components/music/PopularTracks";
import { Play, Loader2, Search, Music2, Disc3, User, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

function greetingKey() {
  const h = new Date().getHours();
  if (h < 12) return "greeting_morning";
  if (h < 19) return "greeting_afternoon";
  return "greeting_evening";
}

const HOME_GENRE_GRADIENTS = [
  "from-[#FF2A4D] to-purple-700", "from-emerald-500 to-teal-800",
  "from-sky-500 to-indigo-800", "from-amber-500 to-red-700",
  "from-fuchsia-500 to-rose-800", "from-lime-500 to-green-800",
  "from-violet-500 to-purple-900", "from-cyan-500 to-blue-800",
];

const SUB_MAP = {
  "Basado en tus gustos": "sub_taste", "Tu mezcla diaria": "sub_daily",
  "Descubre": "sub_discover", "Para empezar": "sub_start",
};

export default function MusicHome() {
  const [data, setData] = useState(null);
  const [mixes, setMixes] = useState([]);
  const [recent, setRecent] = useState([]);
  const { playQueue } = useMusicPlayer();
  const { t } = useI18n();
  const { user } = useAuth();
  // Greet by first name only; the full name is often "Name Surname".
  const firstName = (user && user.name ? user.name.trim().split(/\s+/)[0] : "");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const tid = searchParams.get("track");
    if (!tid) return;
    api.get(`/music/track/${tid}`).then((r) => playQueue([r.data], 0)).catch(() => {});
    searchParams.delete("track");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRecent = () => api.get("/music/history").then((r) => setRecent(r.data.tracks)).catch(() => {});
  const reloadCatalog = () => {
    api.get("/music/home").then((r) => setData(r.data)).catch(() => {});
    api.get("/music/for-you").then((r) => setMixes(r.data.mixes)).catch(() => {});
  };
  const clearHistory = async () => {
    try { await api.delete("/music/history"); setRecent([]); toast.success(t("history_cleared")); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => {
    api.get("/music/home").then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/music/for-you").then((r) => setMixes(r.data.mixes)).catch(() => {});
    loadRecent();
    const h = () => loadRecent();
    window.addEventListener("history-changed", h);
    return () => window.removeEventListener("history-changed", h);
  }, []);

  if (data === null) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  const empty = data && data.total_tracks === 0;


  return (
    <div className="p-4 md:p-8 pb-6">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">SONLYO</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1" data-testid="home-greeting">
            {firstName ? `${t(greetingKey())}, ${firstName}` : t(greetingKey())}
          </h1>
        </div>
        {!empty && (
          <button
            data-testid="home-play-all"
            onClick={() => playQueue(data.recent_tracks, 0)}
            className="bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-full px-6 py-3 font-semibold flex items-center gap-2 transition-colors shadow-[0_0_18px_rgba(255,69,0,0.35)]"
          >
            <Play className="w-4 h-4" fill="white" /> {t("play_all")}
          </button>
        )}
      </div>

      <div className="mb-8 max-w-2xl">
        <p className="text-xs text-zinc-500 mb-2">{t("request_title")}</p>
        <RequestArtist onDone={reloadCatalog} />
      </div>

      {empty ? (
        <div className="text-center py-24">
          <Music2 className="w-12 h-12 text-zinc-700 mx-auto" />
          <h2 className="font-heading text-xl font-semibold mt-4">{t("catalog_empty_t")}</h2>
          <p className="text-zinc-400 mt-2">{t("catalog_empty_d")}</p>
          <Link to="/app/search" className="inline-flex items-center gap-2 mt-5 bg-white text-black rounded-full px-6 py-2.5 font-semibold hover:scale-105 transition-transform">
            <Search className="w-4 h-4" /> {t("go_search")}
          </Link>
        </div>
      ) : (
        <>
          {mixes.length > 0 && (
            <Carousel title={t("made_for_you")} to="/app/for-you" testId="section-mixes">
              {mixes.map((m) => (
                <button key={m.id} data-testid={`mix-card-${m.id}`} onClick={() => playQueue(m.tracks, 0)}
                  className="shrink-0 w-44 bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors text-left group">
                  <div className="relative">
                    {m.cover ? <img src={m.cover} alt="" className="w-36 h-36 rounded-lg object-cover" /> : <div className="w-36 h-36 rounded-lg bg-white/10" />}
                    <span className="absolute bottom-2 right-2 w-10 h-10 rounded-full bg-[#FF2A4D] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all shadow-lg">
                      <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                    </span>
                  </div>
                  <p className="text-sm font-semibold truncate mt-3">{m.title}</p>
                  <p className="text-xs text-zinc-400 truncate">{SUB_MAP[m.subtitle] ? t(SUB_MAP[m.subtitle]) : m.subtitle}</p>
                </button>
              ))}
            </Carousel>
          )}

          {recent.length > 0 && (
            <Carousel
              title={t("recently_played")}
              testId="section-recent"
              action={
                <button data-testid="clear-history-btn" onClick={clearHistory} className="text-xs font-semibold text-zinc-400 hover:text-white flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> {t("clear_history")}
                </button>
              }
            >
              {recent.slice(0, 14).map((tr) => (
                <button key={tr.id} data-testid={`recent-card-${tr.id}`} onClick={() => playQueue(recent, recent.indexOf(tr))}
                  className="shrink-0 w-36 bg-white/5 hover:bg-white/10 rounded-xl p-3 transition-colors text-left">
                  {tr.thumbnail ? <img src={tr.thumbnail} alt="" className="w-full aspect-square rounded-lg object-cover" /> : <div className="w-full aspect-square rounded-lg bg-white/10" />}
                  <p className="text-sm font-semibold truncate mt-2">{tr.title}</p>
                  <p className="text-xs text-zinc-400 truncate">{tr.artist}</p>
                </button>
              ))}
            </Carousel>
          )}

          {data.popular_tracks && data.popular_tracks.length > 0 && (
            <section className="mb-9" data-testid="section-popular">
              <h2 className="font-heading text-xl font-bold mb-3">{t("popular_tracks")}</h2>
              <PopularTracks tracks={data.popular_tracks} />
            </section>
          )}

          {data.artists.length > 0 && (
            <Carousel title={t("artists")} to="/app/artists" testId="section-artists">
              {data.artists.map((a) => (
                <Link key={a.name} to={`/app/artist/${encodeURIComponent(a.name)}`} data-testid={`artist-card-${a.name}`} className="shrink-0 w-40 bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors">
                  {a.thumbnail ? <img src={a.thumbnail} alt="" className="w-32 h-32 rounded-full object-cover mx-auto" /> : <div className="w-32 h-32 rounded-full bg-white/10 flex items-center justify-center mx-auto"><User className="w-10 h-10 text-zinc-500" /></div>}
                  <p className="text-sm font-semibold truncate mt-3 text-center">{a.name}</p>
                  <p className="text-xs text-zinc-400 text-center">{a.track_count} {t("tracks")}</p>
                </Link>
              ))}
            </Carousel>
          )}

          {data.albums.length > 0 && (
            <Carousel title={t("albums")} to="/app/albums" testId="section-albums">
              {data.albums.map((al) => (
                <Link key={al.id} to={`/app/album/${al.id}`} className="shrink-0 w-44 bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors group">
                  <div className="relative">
                    {al.cover ? <img src={al.cover} alt="" className="w-36 h-36 rounded-lg object-cover" /> : <div className="w-36 h-36 rounded-lg bg-white/10 flex items-center justify-center"><Disc3 className="w-10 h-10 text-zinc-500" /></div>}
                  </div>
                  <p className="text-sm font-semibold truncate mt-3">{al.name}</p>
                  <p className="text-xs text-zinc-400 truncate">{al.artist}</p>
                </Link>
              ))}
            </Carousel>
          )}

          {data.genres && data.genres.length > 0 && (
            <section className="mb-9" data-testid="section-genres">
              <div className="flex items-end justify-between mb-3">
                <Link to="/app/browse" className="group flex items-center gap-1.5">
                  <h2 className="font-heading text-xl font-bold">{t("genres")}</h2>
                  <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {data.genres.slice(0, 8).map((g, i) => (
                  <Link
                    key={g.name}
                    to={`/app/genre/${encodeURIComponent(g.name)}`}
                    data-testid={`home-genre-${g.name}`}
                    className={`relative overflow-hidden rounded-xl aspect-[16/7] p-4 flex items-end bg-gradient-to-br ${HOME_GENRE_GRADIENTS[i % HOME_GENRE_GRADIENTS.length]} hover:scale-[1.02] transition-transform`}
                  >
                    <span className="font-heading font-bold drop-shadow truncate">{g.name}</span>
                    <span className="absolute top-2 right-2 text-[11px] bg-black/35 rounded-full px-2 py-0.5">{g.track_count}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="font-heading text-xl font-bold mb-2">{t("recently_added")}</h2>
            <div>
              {data.recent_tracks.map((t2, i) => (
                <TrackRow key={t2.id} track={t2} index={i} tracks={data.recent_tracks} />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
